import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Provider,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { AbstractControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { AlertComponent, CheckboxComponent, FormGroupComponent, FormsUtil, ModalService, RadioComponent } from 'mbs-ui-kit';
import { AzureAccessTierType, StorageClass, StorageType, TypesForFFI } from 'mbs-ui-shared@models';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { AdvancedSettingsVirtualStepValue, VMQuiesceType } from 'wizards/models/advanced-settings-vm-models';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WindowsFeatures, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';
import { AdvancedOptionsStepLogic } from '../advanced-options-step/advanced-options-step.logic';
import { AdvancedOptionsStorageClassAzureTierComponent } from '../advanced-options-step/storage-class-azure-tier/advanced-options-storage-class-azure-tier.component';

const AdvancedSettingsVMStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => AdvancedSettingsVirtualStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-advanced-settings-virtual-step',
  templateUrl: './advanced-settings-virtual-step.component.html',
  providers: [AdvancedSettingsVMStepValueAccessor],
  imports: [
    AlertComponent,
    CheckboxComponent,
    CommonModule,
    FormGroupComponent,
    NgbTooltipModule,
    RadioComponent,
    ReactiveFormsModule,
    AdvancedOptionsStorageClassAzureTierComponent,
    I18NextPipe
  ]
})
export class AdvancedSettingsVirtualStepComponent extends StepBase<AdvancedSettingsVirtualStepValue> implements OnInit, OnChanges {
  ability = inject(AbilityService);
  i18nextService = inject(I18NextService);
  i18nPipe = inject(I18NextPipe);
  mainService: RemoteManagementWizardsService;
  modalService = inject(ModalService);
  stepService = inject(WizardStepsService);

  @ViewChild('errorSyntheticFull', { static: true, read: TemplateRef }) errorSyntheticFull: TemplateRef<HTMLElement>;

  @Input() advancedOptionsFlags = null;
  @Input() selectedStorage: StorageConnection;

  @Output() changeStorageType = new EventEmitter<{
    azureType?: AzureAccessTierType;
    isStorageBadForFFI: boolean;
    storageClass: StorageClass;
  }>();

  public advancedOptionsStepLogic: AdvancedOptionsStepLogic;
  public isCBTAvailable = true;
  public storageType = StorageType;
  public vmQuiesceType = VMQuiesceType;
  public elementSelectors = {
    id: {
      VMWareFormGroup: 'vmware-form-group',
      quiesceTypeRadio1: 'quiesce-type-radio-1',
      quiesceTypeRadio2: 'quiesce-type-radio-2',
      quiesceTypeRadio3: 'quiesce-type-radio-3',
      CBTCheckbox: 'advanced_settings_block_tracking_checkbox'
    },
    name: {
      CBTCheckDoxDisableTooltip: 'advanced_settings_block_tracking_checkbox_disable_tooltip'
    },
    css: {
      appAwareInfoPanel: 'app-aware-info-panel'
    }
  };

  private verifiedStorageClassOrAzure = false;

  syntheticFullValidator(control: AbstractControl): ValidationErrors | null {
    if (
      this.selectedStorage?.StorageType !== StorageType.AmazonS3 &&
      this.selectedStorage?.StorageType !== StorageType.AmazonS3Eu &&
      this.selectedStorage?.StorageType !== StorageType.Azure
    ) {
      return null;
    }

    if (control.value && !this.verifiedStorageClassOrAzure) {
      const storageClass = this.stepForm.get('storageClass')?.value;

      if (TypesForFFI.unsupported.Amazon.includes(storageClass) || TypesForFFI.unsupported.Azure.includes(storageClass)) {
        return { invalidClass: true };
      }
    }

    return null;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();
    this.mainService = mainService;

    this.advancedOptionsStepLogic = new AdvancedOptionsStepLogic(this.modalService, this.stepService, this.i18nPipe, this);
  }

  ngOnInit(): void {
    this.initForm();
    this.initEvents();

    if (this.isHyperV && !this.isOffline) this.checkCBT();
  }

  initForm(): void {
    this.stepForm = this.advancedOptionsStepLogic.getForm('vm');
    this.stepForm.get('syntheticFull').addValidators(this.syntheticFullValidator.bind(this));

    this.initFormEvents();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.stepForm && changes?.selectedStorage) {
      this.changeDisabledStateControls();
      const selectedStorage: StorageConnection =
        (changes.selectedStorage.currentValue as unknown as StorageConnection) || this.selectedStorage;

      if (selectedStorage?.StorageType === StorageType.Azure) {
        this.azureAccessTierChangeHandler(this.stepForm.get('azureAccessTier').value);
      } else if (selectedStorage?.StorageType === StorageType.AmazonS3 || selectedStorage?.StorageType === StorageType.AmazonS3Eu) {
        this.storageClassChangeHandler(this.stepForm.get('storageClass').value);
      } else {
        this.changeStorageType.emit({
          azureType: null,
          isStorageBadForFFI: false,
          storageClass: null
        });
      }
    }

    if (!this.confirmedAsEnabled && changes?.stepFocused?.currentValue) {
      this.showEnableStepModal(this.modalService);
    }

    if (this.stepForm && changes.advancedOptionsFlags) {
      this.syntheticFullSettingPossible.next(changes.advancedOptionsFlags.currentValue.SyntheticFullSettingPossible);
    }
  }

  updateForm(value: AdvancedSettingsVirtualStepValue): void {
    if (!value.dataFromHelper) {
      return;
    }

    this.initialSyntheticFull = !!value.syntheticFull; // Should be before change form

    if (this.isRDMode) {
      this.advancedOptionsStepLogic.resetStorageClassesForRD();
    }

    this.stepForm.reset(value);

    this.setValuesFromObjOrDefault(value);
  }

  forceValid(step: ForceValidType = null): void {
    if (step.isNext && this.stepForm.get('syntheticFull').invalid) {
      this.showSyntheticFullModal(step.isNext);
    } else {
      FormsUtil.triggerValidation(this.stepForm);
      this.resetValidStateForValidFields();
    }
  }

  showSyntheticFullModal(needNextStep: boolean): void {
    this.advancedOptionsStepLogic
      .showSyntheticFullModal(this.errorSyntheticFull)
      .then((confirm) => {
        if (confirm) {
          this.verifiedStorageClassOrAzure = true;
          this.stepForm.get('syntheticFull').setValue(false);
          this.stepForm.updateValueAndValidity();
          if (needNextStep) this.nextStep.emit();
        }
      })
      .catch(() => this.stepForm.updateValueAndValidity());
  }

  storageClassChangeHandler(event: StorageClass): void {
    this.advancedOptionsStepLogic.storageClassChangeHandler(event, true);
  }

  azureAccessTierChangeHandler(event: AzureAccessTierType): void {
    this.advancedOptionsStepLogic.azureAccessTierChangeHandler(event, true);
  }

  syntheticFullChangeHandler(event): void {
    if (!event) {
      return;
    }

    const final = () => {
      this.verifiedStorageClassOrAzure = false;
      this.stepForm.get('syntheticFull').updateValueAndValidity();
    };

    let specialStorage = false;
    let standardStorageName = '';

    if (
      this.selectedStorage?.StorageType === this.storageType.AmazonS3 ||
      this.selectedStorage?.StorageType === this.storageType.AmazonS3Eu
    ) {
      const selectedClass = this.stepForm.get('storageClass').value;

      specialStorage = this.advancedOptionsStepLogic.expensiveStorageClasses.S3.includes(selectedClass);
      standardStorageName = String(this.advancedOptionsStepLogic.StorageClasses.find((item) => item.value === StorageClass.STANDARD).label);
    }

    if (this.selectedStorage?.StorageType === this.storageType.Azure) {
      const selectedClass = this.stepForm.get('azureAccessTier').value;

      specialStorage = this.advancedOptionsStepLogic.expensiveStorageClasses.Azure.includes(selectedClass);
      standardStorageName = String(this.advancedOptionsStepLogic.AccessTires.find((item) => item.value === AzureAccessTierType.Hot).label);
    }

    if (this.stepFocused && specialStorage) {
      return void AdvancedOptionsStepLogic.showStorageCostImpact(standardStorageName, this.i18nPipe, this.modalService)
        .then((result) => {
          this.stepForm.get('syntheticFull').setValue(result);
        })
        .catch(() => this.stepForm.get('syntheticFull').setValue(false))
        .finally(final);
    }
  }

  changeDisabledStateControls(): void {
    if (this.selectedStorage?.StorageType === StorageType.AmazonS3 || this.selectedStorage?.StorageType === StorageType.AmazonS3Eu) {
      this.stepForm.get('useS3Acceleration').enable();
      this.stepForm.get('storageClass').enable();
    } else {
      this.stepForm.get('useS3Acceleration').disable();
      this.stepForm.get('storageClass').disable();
    }

    if (this.selectedStorage?.StorageType === StorageType.Azure) {
      this.stepForm.get('azureAccessTier').enable();
    } else {
      this.stepForm.get('azureAccessTier').disable();
    }
  }

  private initEvents(): void {
    this.advancedOptionsStepLogic.initEvents(this.syntheticFullSettingPossible, true);
  }

  private checkCBT(): void {
    this.stepService
      .VMFeatureTest(this.mainService.hid, WindowsFeatures.hyperVChangedBlockTracking)
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.isCBTAvailable = !!res?.data;
        this.toggleFormControls(['useChangeBlockTracking'], this.isCBTAvailable);

        if (!this.isCBTAvailable) {
          this.stepForm.get('useChangeBlockTracking').setValue(false);
        }
      });
  }

  private setValuesFromObjOrDefault(value: AdvancedSettingsVirtualStepValue): void {
    this.advancedOptionsStepLogic.setValuesFromObjOrDefault(value, this.isCreate, true);
  }
}
